<!-- eslint-disable import-alias/import-alias -->
<script lang="ts" setup>
import { onBeforeMount, onMounted, onUnmounted, watch, inject, computed } from 'vue';
import { useNotificationStore } from '@/store/notificationStore';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAnalytics } from '@/composables/useAnalytics';
import { useCookiebot } from '@/composables/useCookiebot';
import { useLanguage } from '@/composables/useLanguage';
import { useGtm } from '@gtm-support/vue-gtm';
import LoginView from './views/newlogin/LoginView.vue';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useLogin } from '@/login/composables/useLogin';
import { RouteNames } from './models/enums/RouteNames';
import { useUser } from '@/login/composables/useUser';
import { useStartdeliver } from './composables/useStartdeliver';
import { useSatismeter } from './composables/useSatismeter';
import InstanceChangedWarning from './views/InstanceChangedWarning.vue';
import type { EventService } from './services/EventService';
import type { RightsService } from './services/RightsService';
import { EntityCache } from '@sysarb/sysarb-api/cache';
import { useSaltfish } from './composables/useSaltfish';
import { useOpentelemetry } from './composables/useOpentelemetry';
import { useServiceWorkerUpdate } from './composables/useServiceWorkerUpdate';
import { clearReloadCount } from '@/chunkErrorRecovery';
import { useTanstackCache } from './api/TanstackCache';
import ConsoleLoggerControlPanel from './components/ConsoleLoggerControlPanel.vue';

useServiceWorkerUpdate();

console.info({ cat: 'Global', msg: '-- DEV MODE ON --' });

useLogin();
useTanstackCache();

const loginStore = useLoginStore();
const { userData, isLoggedIn, isInstanceSelected, role, instancesLoading, resgateAuthenticated, instanceTopic, selectedInstance, lastAuthenticatedInstance, instance, showInstanceChangedWarning } = storeToRefs(loginStore);
const { isSupport, isActiveInstance, isConsultant } = useUser();
const notificationStore = useNotificationStore();
const { init: initAnalytics } = useAnalytics();
const { init: initStartdeliver, event } = useStartdeliver();
const { init: initSatismeter } = useSatismeter();
const { init: initSaltfish } = useSaltfish();
const { init: initOpentelemetry } = useOpentelemetry();

const gtm = useGtm();
gtm?.debug(import.meta.env.DEV === true);

const router = useRouter();

const eventService = inject<EventService>('eventService');
const rightsService = inject<RightsService>('rightsService');

const { cookieBot } = useCookiebot(true);
const { language, updateLanguage } = useLanguage();

const requiresAuth = computed(() => {
    if (router.currentRoute.value.name === RouteNames.Login) return true;
    return router.currentRoute.value.meta.requiresAuth;
});

const showLoginView = computed(() => {
    if (requiresAuth.value === false) return false;
    if (isLoggedIn.value === false) return true;
    if (isInstanceSelected.value === false) return true;
    if (instancesLoading.value > 0) return true;

    return false;
});

const isDev = computed(() => import.meta.env.DEV);

const showLoggedInView = computed(() => {
    if (requiresAuth.value === false) return true;
    if (instanceTopic.value == null) return false;
    if (userData.value == null) return false;
    if (selectedInstance.value == null) return false;
    if (instance.value !== lastAuthenticatedInstance.value) return false;
    if (resgateAuthenticated.value === false) return false;

    return true;
});

onBeforeMount(() => {
    notificationStore.clear();
});

onMounted((): void => {
    clearReloadCount();
    eventService?.startEventTimer();

    // remove loading screen when app is ready.
    setTimeout(
        () => {
            const loadingScreen = document.getElementById('app-loading-screen');
            if (loadingScreen != null) {
                document.body.removeChild(loadingScreen);
            }
        },
        1000
    );

    window.showDebug = gtm?.debug;
});

onUnmounted(() => {
    eventService?.endEventTimer();
});

watch(() => cookieBot.value, () => {
    if (cookieBot.value.statistics) {
        initAnalytics();
        initStartdeliver();
        initSatismeter();
        initSaltfish();
        initOpentelemetry();
    }
}, { immediate: true, deep: true });

watch(() => language.value, () => {
    void updateLanguage(language.value);
}, {
    deep: true,
    immediate: true,
    once: true,
});

watch(() => role.value, () => {
    if (role.value == null) return;
    rightsService?.clearRights();
    EntityCache.clear();
});

watch(() => showLoggedInView.value, () => {
    if (showLoggedInView.value === false) return;
    event('loggedIn');
}, { immediate: true });

watch(() => isActiveInstance.value, () => {
    if (showLoggedInView.value === false) return;
    if (isSupport.value === true) return;
    if (isConsultant.value === true) return;
    if (isActiveInstance.value === true) return;

    console.error('The instance is not active. Redirecting.', selectedInstance.value);
    loginStore.setInstance(null);
}, { immediate: true });

</script>

<template>
    <ConsoleLoggerControlPanel v-if="isDev" />
    <InstanceChangedWarning v-if="showInstanceChangedWarning" />
    <template v-else>
        <LoginView v-if="showLoginView === true" />

        <router-view v-else-if="showLoggedInView" />
    </template>
</template>

<style lang="scss">
@import "./styles/app.scss";
</style>
